import React, { createContext, useState, useContext, useEffect } from 'react';
import supabase from '../lib/supabase';

// Create context
const AttendanceContext = createContext();

// Create provider component
export const AttendanceProvider = ({ children }) => {
  const [data, setData] = useState({
    members: [],
    weeklyAttendance: [],
    cancelledMeetings: [],
    currentQuarter: 'Q3 2025',
    loading: true,
    error: null
  });

  // Load all data on mount
  useEffect(() => {
    loadAllData();
  }, []);

  // Load all data from database
  const loadAllData = async () => {
    try {
      setData(prev => ({ ...prev, loading: true, error: null }));

      // First, check if the required tables exist
      await createTablesIfNotExist();

      // Then fetch the data
      const [membersData, attendanceData, cancelledMeetingsData] = await Promise.all([
        fetchMembers(),
        fetchAttendance(),
        fetchCancelledMeetings()
      ]);

      // Create sample data if tables are empty
      let finalMembersData = membersData;
      let finalAttendanceData = attendanceData;

      if (membersData.length === 0) {
        finalMembersData = await createSampleMembers();
      }

      if (attendanceData.length === 0) {
        finalAttendanceData = await createSampleAttendance(finalMembersData);
      }

      console.log('Data loaded successfully:', {
        members: finalMembersData.length,
        attendance: finalAttendanceData.length,
        cancelled: cancelledMeetingsData?.length || 0
      });

      setData(prev => ({
        ...prev,
        members: finalMembersData,
        weeklyAttendance: finalAttendanceData,
        cancelledMeetings: cancelledMeetingsData,
        loading: false,
        currentQuarter: 'Q3 2025'
      }));
    } catch (error) {
      console.error('Error loading data:', error);
      setData(prev => ({
        ...prev,
        error: error.message || 'Failed to load data. Please try again.',
        loading: false
      }));
    }
  };

  // Create tables if they don't exist
  const createTablesIfNotExist = async () => {
    try {
      // Check if members table exists
      const { count: membersCount, error: membersError } = await supabase
        .from('members_moco_png')
        .select('*', { count: 'exact', head: true });

      if (membersError && membersError.code === '42P01') {
        console.log('Creating members table...');
        await createMembersTable();
      } else {
        await ensureMembersTableColumns();
      }

      // Check if attendance table exists
      const { count: attendanceCount, error: attendanceError } = await supabase
        .from('attendance_moco_png')
        .select('*', { count: 'exact', head: true });

      if (attendanceError && attendanceError.code === '42P01') {
        console.log('Creating attendance table...');
        await createAttendanceTable();
      } else {
        await fixAttendanceTableStructure();
      }

      // Check if cancelled meetings table exists
      const { count: cancelledCount, error: cancelledError } = await supabase
        .from('cancelled_meetings_moco_png')
        .select('*', { count: 'exact', head: true });

      if (cancelledError && cancelledError.code === '42P01') {
        console.log('Creating cancelled meetings table...');
        await createCancelledMeetingsTable();
      }

      return true;
    } catch (error) {
      console.error('Error creating tables:', error);
      throw new Error('Failed to initialize database. Please contact support.');
    }
  };

  // Create members table
  const createMembersTable = async () => {
    try {
      const { error } = await supabase.rpc('create_members_table');
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error creating members table:', error);
      throw error;
    }
  };

  // Create attendance table
  const createAttendanceTable = async () => {
    try {
      const { error } = await supabase.rpc('create_attendance_table');
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error creating attendance table:', error);
      throw error;
    }
  };

  // Create cancelled meetings table
  const createCancelledMeetingsTable = async () => {
    try {
      const { error } = await supabase.rpc('create_cancelled_meetings_table');
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error creating cancelled meetings table:', error);
      throw error;
    }
  };

  // Ensure members table has all required columns
  const ensureMembersTableColumns = async () => {
    try {
      const { error } = await supabase.rpc('ensure_members_table_columns');
      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error ensuring members table columns:', error);
      return false;
    }
  };

  // Fix attendance table structure if needed
  const fixAttendanceTableStructure = async () => {
    try {
      const { error } = await supabase.rpc('fix_attendance_table');
      if (error) {
        console.error('Error with RPC fix_attendance_table:', error);
        // If RPC fails, execute direct SQL
        const { error: sqlError } = await supabase.from('attendance_moco_png').select('id').limit(1);
        if (sqlError && sqlError.message.includes('attendees')) {
          const { error: alterError } = await supabase.rpc('manual_add_attendees_column');
          if (alterError) throw alterError;
        }
      }
      return true;
    } catch (error) {
      console.error('Error fixing attendance table structure:', error);
      return false;
    }
  };

  // Start a new quarter
  const startNewQuarter = async () => {
    try {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth();
      let quarter;
      
      if (month < 3) quarter = `Q1 ${year}`;
      else if (month < 6) quarter = `Q2 ${year}`;
      else if (month < 9) quarter = `Q3 ${year}`;
      else quarter = `Q4 ${year}`;

      const { error } = await supabase
        .from('members_moco_png')
        .update({
          consecutiveAbsences: 0,
          active: true,
          removedDate: null
        });

      if (error) throw error;

      setData(prev => ({
        ...prev,
        currentQuarter: quarter,
        members: prev.members.map(m => ({
          ...m,
          consecutiveAbsences: 0,
          active: true,
          removedDate: null
        }))
      }));

      return true;
    } catch (error) {
      console.error('Error starting new quarter:', error);
      throw error;
    }
  };

  // Create sample members if table is empty
  const createSampleMembers = async () => {
    try {
      const sampleMembers = [
        {
          name: 'John Smith',
          email: 'john.smith@example.com',
          phone: '301-555-1234',
          company: 'Tech Solutions Inc',
          active: true,
          consecutiveAbsences: 0
        },
        {
          name: 'Emily Johnson',
          email: 'emily.j@example.com',
          phone: '301-555-2345',
          company: 'Marketing Experts LLC',
          active: true,
          consecutiveAbsences: 0
        },
        {
          name: 'Michael Rodriguez',
          email: 'mrodriguez@example.com',
          phone: '301-555-3456',
          company: 'Financial Advisors Group',
          active: true,
          consecutiveAbsences: 0
        },
        {
          name: 'Sarah Williams',
          email: 'swilliams@example.com',
          phone: '301-555-4567',
          company: 'Legal Partners',
          active: true,
          consecutiveAbsences: 0
        },
        {
          name: 'David Chen',
          email: 'dchen@example.com',
          phone: '301-555-5678',
          company: 'Healthcare Solutions',
          active: true,
          consecutiveAbsences: 0
        },
      ];

      const { data, error } = await supabase
        .from('members_moco_png')
        .insert(sampleMembers)
        .select();

      if (error) {
        console.error('Error creating sample members:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      console.error('Error creating sample members:', error);
      return [];
    }
  };

  // Create sample attendance if table is empty
  const createSampleAttendance = async (members) => {
    try {
      if (!members || members.length === 0) return [];

      const lastWeek = new Date();
      lastWeek.setDate(lastWeek.getDate() - 7);
      lastWeek.setDate(lastWeek.getDate() - (lastWeek.getDay() + 5) % 7);

      const membersToAttend = members.slice(0, 3);
      const attendees = membersToAttend.map(member => ({
        memberId: member.id,
        checkInTime: new Date(lastWeek.getTime() + 8 * 60 * 60 * 1000).toISOString(),
        minutesLate: 0
      }));

      const { data, error } = await supabase
        .from('attendance_moco_png')
        .insert([{
          date: lastWeek.toISOString().split('T')[0],
          isActive: false,
          attendees: attendees
        }])
        .select();

      if (error) {
        console.error('Error creating sample attendance:', error);
        return [];
      }

      return data.map(meeting => ({
        ...meeting,
        attendees: typeof meeting.attendees === 'string' 
          ? JSON.parse(meeting.attendees) 
          : meeting.attendees || []
      }));
    } catch (error) {
      console.error('Error creating sample attendance:', error);
      return [];
    }
  };

  // Fetch members from database
  const fetchMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('members_moco_png')
        .select('*')
        .order('name');

      if (error) {
        console.error('Error details:', error);
        if (error.code === '42P01') {
          return [];
        }
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching members:', error);
      return [];
    }
  };

  // Fetch attendance data from database
  const fetchAttendance = async () => {
    try {
      const { data, error } = await supabase
        .from('attendance_moco_png')
        .select('*')
        .order('date', { ascending: false });

      if (error) {
        console.error('Error details:', error);
        if (error.code === '42P01') {
          return [];
        }
        throw error;
      }

      return (data || []).map(meeting => ({
        ...meeting,
        attendees: typeof meeting.attendees === 'string' 
          ? JSON.parse(meeting.attendees) 
          : meeting.attendees || []
      }));
    } catch (error) {
      console.error('Error fetching attendance:', error);
      return [];
    }
  };

  // Fetch cancelled meetings from database
  const fetchCancelledMeetings = async () => {
    try {
      const { data, error } = await supabase
        .from('cancelled_meetings_moco_png')
        .select('*');

      if (error) {
        console.error('Error details:', error);
        if (error.code === '42P01') {
          return [];
        }
        throw error;
      }

      return data || [];
    } catch (error) {
      console.error('Error fetching cancelled meetings:', error);
      return [];
    }
  };

  // Get today's meeting or return null if no meeting today
  const getTodaysMeeting = () => {
    if (!data.weeklyAttendance || data.weeklyAttendance.length === 0) {
      return null;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return data.weeklyAttendance.find(meeting => {
      if (!meeting || !meeting.date) return false;
      const meetingDate = new Date(meeting.date);
      meetingDate.setHours(0, 0, 0, 0);
      return meetingDate.getTime() === today.getTime();
    });
  };

  // Check if today is Tuesday (meeting day)
  const isTuesday = () => {
    const today = new Date();
    return today.getDay() === 2;
  };

  // Check if it's currently meeting time (7:00 AM - 10:00 AM)
  const isMeetingTime = () => {
    const now = new Date();
    const hour = now.getHours();
    return hour >= 7 && hour < 10;
  };

  // Check if today's meeting is cancelled
  const isTodayMeetingCancelled = () => {
    if (!isTuesday()) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];

    return (data.cancelledMeetings || []).some(m => m.date === todayStr);
  };

  // Cancel today's meeting
  const cancelTodaysMeeting = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];

      const { data: newCancellation, error } = await supabase
        .from('cancelled_meetings_moco_png')
        .insert([{ date: todayStr }])
        .select()
        .single();

      if (error) throw error;

      setData(prev => ({
        ...prev,
        cancelledMeetings: [...(prev.cancelledMeetings || []), newCancellation]
      }));

      return true;
    } catch (error) {
      console.error('Error cancelling meeting:', error);
      throw error;
    }
  };

  // Uncancel today's meeting
  const uncancelTodaysMeeting = async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().split('T')[0];

      const { error } = await supabase
        .from('cancelled_meetings_moco_png')
        .delete()
        .eq('date', todayStr);

      if (error) throw error;

      setData(prev => ({
        ...prev,
        cancelledMeetings: (prev.cancelledMeetings || []).filter(m => m.date !== todayStr)
      }));

      return true;
    } catch (error) {
      console.error('Error uncancelling meeting:', error);
      throw error;
    }
  };

  // Check in a member to today's meeting
  const checkInMember = async (memberId, checkInTime) => {
    try {
      let meeting = getTodaysMeeting();

      if (!meeting) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const { data: newMeeting, error } = await supabase
          .from('attendance_moco_png')
          .insert([{
            date: today.toISOString().split('T')[0],
            isActive: true,
            attendees: []
          }])
          .select()
          .single();

        if (error) throw error;

        meeting = {
          ...newMeeting,
          attendees: typeof newMeeting.attendees === 'string' 
            ? JSON.parse(newMeeting.attendees) 
            : newMeeting.attendees || []
        };

        setData(prev => ({
          ...prev,
          weeklyAttendance: [...prev.weeklyAttendance, meeting]
        }));
      }

      if (!memberId) return meeting;

      const now = checkInTime || new Date();
      const meetingStart = new Date(meeting.date);
      meetingStart.setHours(8, 5, 0, 0);

      const minutesLate = (now.getHours() > 8 || (now.getHours() === 8 && now.getMinutes() >= 5))
        ? Math.floor((now - meetingStart) / (1000 * 60))
        : 0;

      const attendees = Array.isArray(meeting.attendees) ? meeting.attendees : [];
      const alreadyCheckedIn = attendees.some(a => a.memberId === memberId);

      if (alreadyCheckedIn) {
        throw new Error('You have already checked in for this meeting');
      }

      const updatedAttendees = [
        ...attendees,
        {
          memberId,
          checkInTime: now.toISOString(),
          minutesLate
        }
      ];

      const { data: updatedMeeting, error } = await supabase
        .from('attendance_moco_png')
        .update({ attendees: updatedAttendees })
        .eq('id', meeting.id)
        .select()
        .single();

      if (error) throw error;

      const parsedMeeting = {
        ...updatedMeeting,
        attendees: typeof updatedMeeting.attendees === 'string' 
          ? JSON.parse(updatedMeeting.attendees) 
          : updatedMeeting.attendees || []
      };

      setData(prev => ({
        ...prev,
        weeklyAttendance: prev.weeklyAttendance.map(m => 
          m.id === meeting.id ? parsedMeeting : m
        )
      }));

      return true;
    } catch (error) {
      console.error('Error checking in member:', error);
      throw error;
    }
  };

  // End the meeting and process absences
  const endMeeting = async () => {
    try {
      const meeting = getTodaysMeeting();
      if (!meeting) throw new Error('No active meeting found');

      const attendeeIds = (Array.isArray(meeting.attendees) ? meeting.attendees : [])
        .map(a => a.memberId);

      const absentMembers = data.members
        .filter(m => m.active)
        .filter(m => !attendeeIds.includes(m.id));

      for (const member of absentMembers) {
        const consecutiveAbsences = (member.consecutiveAbsences || 0) + 1;
        const active = consecutiveAbsences < 3;
        const removedDate = active ? null : new Date().toISOString();

        await supabase
          .from('members_moco_png')
          .update({
            consecutiveAbsences,
            active,
            removedDate: !active ? removedDate : null
          })
          .eq('id', member.id);
      }

      for (const memberId of attendeeIds) {
        await supabase
          .from('members_moco_png')
          .update({ consecutiveAbsences: 0 })
          .eq('id', memberId);
      }

      const { data: updatedMeeting, error } = await supabase
        .from('attendance_moco_png')
        .update({ isActive: false })
        .eq('id', meeting.id)
        .select()
        .single();

      if (error) throw error;

      await loadAllData();

      return true;
    } catch (error) {
      console.error('Error ending meeting:', error);
      throw error;
    }
  };

  // Get active members
  const getActiveMembers = () => {
    return (data.members || []).filter(m => m.active);
  };

  // Get members needing outreach (1+ consecutive absences)
  const getMembersNeedingOutreach = () => {
    return (data.members || []).filter(
      m => m.active && m.consecutiveAbsences && m.consecutiveAbsences > 0
    );
  };

  // Add a new member
  const addMember = async (memberData) => {
    try {
      console.log('Adding member with data:', memberData);

      if (!memberData.name || !memberData.email) {
        throw new Error('Name and email are required');
      }

      const memberToAdd = {
        name: memberData.name,
        email: memberData.email,
        phone: memberData.phone || null,
        company: memberData.company || null,
        active: true,
        consecutiveAbsences: 0
      };

      console.log('Member object to insert:', memberToAdd);

      const { data: newMember, error } = await supabase
        .from('members_moco_png')
        .insert([memberToAdd])
        .select()
        .single();

      if (error) {
        console.error('Supabase error adding member:', error);
        throw new Error(`Failed to add member: ${error.message}`);
      }

      if (!newMember) {
        throw new Error('No data returned from insert operation');
      }

      console.log('Successfully added member:', newMember);

      setData(prev => ({
        ...prev,
        members: [...prev.members, newMember]
      }));

      return newMember;
    } catch (error) {
      console.error('Error adding member:', error);
      throw error;
    }
  };

  // Update a member
  const updateMember = async (id, memberData) => {
    try {
      if (!id) {
        throw new Error('Member ID is required for update');
      }

      console.log('Updating member with ID:', id, 'Data:', memberData);

      const updateData = {
        name: memberData.name,
        email: memberData.email
      };

      if (memberData.phone !== undefined) {
        updateData.phone = memberData.phone || null;
      }

      if (memberData.company !== undefined) {
        updateData.company = memberData.company || null;
      }

      const { data: updatedMember, error } = await supabase
        .from('members_moco_png')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Supabase error updating member:', error);
        throw new Error(`Failed to update member: ${error.message}`);
      }

      if (!updatedMember) {
        throw new Error('No data returned from update operation');
      }

      console.log('Successfully updated member:', updatedMember);

      setData(prev => ({
        ...prev,
        members: prev.members.map(m => m.id === id ? updatedMember : m)
      }));

      return updatedMember;
    } catch (error) {
      console.error('Error updating member:', error);
      throw error;
    }
  };

  // Delete a member
  const deleteMember = async (id) => {
    try {
      if (!id) {
        throw new Error('Member ID is required for deletion');
      }

      console.log('Deleting member with ID:', id);

      const { error } = await supabase
        .from('members_moco_png')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Supabase error deleting member:', error);
        throw new Error(`Failed to delete member: ${error.message}`);
      }

      console.log('Successfully deleted member with ID:', id);

      setData(prev => ({
        ...prev,
        members: prev.members.filter(m => m.id !== id)
      }));

      return true;
    } catch (error) {
      console.error('Error deleting member:', error);
      throw error;
    }
  };

  // Add a historical meeting with retry logic
  const addHistoricalMeeting = async (meetingData) => {
    try {
      console.log('Adding historical meeting:', meetingData);

      // Make sure the date is treated as a local date without timezone adjustment
      const formattedData = {
        date: meetingData.date,
        isActive: false,
        attendees: meetingData.attendees || []
      };

      console.log('Formatted meeting data:', formattedData);

      // Try the insert with retry logic for schema cache issues
      let retryCount = 0;
      let lastError = null;

      while (retryCount < 3) {
        try {
          const { data, error } = await supabase
            .from('attendance_moco_png')
            .insert([formattedData])
            .select();

          if (error) {
            if (error.message.includes('schema cache') && retryCount < 2) {
              console.log(`Schema cache error, retrying... (${retryCount + 1}/3)`);
              retryCount++;
              await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
              continue;
            }
            throw error;
          }

          if (!data || data.length === 0) {
            throw new Error('No data returned from insert operation');
          }

          const newMeeting = {
            ...data[0],
            attendees: typeof data[0].attendees === 'string' 
              ? JSON.parse(data[0].attendees) 
              : data[0].attendees || []
          };

          console.log('Successfully added historical meeting:', newMeeting);

          setData(prev => ({
            ...prev,
            weeklyAttendance: [...prev.weeklyAttendance, newMeeting]
          }));

          return newMeeting;
        } catch (error) {
          lastError = error;
          retryCount++;
          if (retryCount < 3) {
            console.log(`Error adding meeting, retrying... (${retryCount}/3):`, error.message);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }

      throw lastError;
    } catch (error) {
      console.error('Error in addHistoricalMeeting:', error);
      throw error;
    }
  };

  // Update a historical meeting
  const updateHistoricalMeeting = async (id, meetingData) => {
    try {
      console.log('Updating historical meeting:', id, meetingData);

      const formattedData = {
        date: meetingData.date,
        attendees: meetingData.attendees || []
      };

      console.log('Formatted update data:', formattedData);

      const { data, error } = await supabase
        .from('attendance_moco_png')
        .update(formattedData)
        .eq('id', id)
        .select();

      if (error) {
        console.error('Error updating historical meeting:', error);
        throw new Error(`Failed to update meeting: ${error.message}`);
      }

      if (!data || data.length === 0) {
        throw new Error('No data returned from update operation');
      }

      const updatedMeeting = {
        ...data[0],
        attendees: typeof data[0].attendees === 'string' 
          ? JSON.parse(data[0].attendees) 
          : data[0].attendees || []
      };

      console.log('Successfully updated historical meeting:', updatedMeeting);

      setData(prev => ({
        ...prev,
        weeklyAttendance: prev.weeklyAttendance.map(m => 
          m.id === id ? updatedMeeting : m
        )
      }));

      return updatedMeeting;
    } catch (error) {
      console.error('Error in updateHistoricalMeeting:', error);
      throw error;
    }
  };

  // Delete a historical meeting
  const deleteHistoricalMeeting = async (id) => {
    try {
      console.log('Deleting historical meeting:', id);

      const { error } = await supabase
        .from('attendance_moco_png')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting historical meeting:', error);
        throw new Error(`Failed to delete meeting: ${error.message}`);
      }

      console.log('Successfully deleted historical meeting:', id);

      setData(prev => ({
        ...prev,
        weeklyAttendance: prev.weeklyAttendance.filter(m => m.id !== id)
      }));

      return true;
    } catch (error) {
      console.error('Error in deleteHistoricalMeeting:', error);
      throw error;
    }
  };

  // Update meeting attendance for a specific member
  const updateMeetingAttendance = async (meetingId, memberId, attended, minutesLate = 0) => {
    try {
      const meeting = data.weeklyAttendance.find(m => m.id === meetingId);
      if (!meeting) throw new Error('Meeting not found');

      let attendees = Array.isArray(meeting.attendees) ? [...meeting.attendees] : [];

      if (attended) {
        const existingIndex = attendees.findIndex(a => a.memberId === memberId);
        if (existingIndex >= 0) {
          attendees[existingIndex] = {
            ...attendees[existingIndex],
            minutesLate
          };
        } else {
          attendees.push({
            memberId,
            checkInTime: new Date().toISOString(),
            minutesLate
          });
        }
      } else {
        attendees = attendees.filter(a => a.memberId !== memberId);
      }

      const { data: updatedMeeting, error } = await supabase
        .from('attendance_moco_png')
        .update({ attendees })
        .eq('id', meetingId)
        .select()
        .single();

      if (error) throw error;

      const parsedMeeting = {
        ...updatedMeeting,
        attendees: typeof updatedMeeting.attendees === 'string' 
          ? JSON.parse(updatedMeeting.attendees) 
          : updatedMeeting.attendees || []
      };

      setData(prev => ({
        ...prev,
        weeklyAttendance: prev.weeklyAttendance.map(m => 
          m.id === meetingId ? parsedMeeting : m
        )
      }));

      return parsedMeeting;
    } catch (error) {
      console.error('Error updating meeting attendance:', error);
      throw error;
    }
  };

  // Delete meeting attendance for a specific member
  const deleteMeetingAttendance = async (meetingId, memberId) => {
    try {
      const meeting = data.weeklyAttendance.find(m => m.id === meetingId);
      if (!meeting) throw new Error('Meeting not found');

      const attendees = (Array.isArray(meeting.attendees) ? meeting.attendees : [])
        .filter(a => a.memberId !== memberId);

      const { data: updatedMeeting, error } = await supabase
        .from('attendance_moco_png')
        .update({ attendees })
        .eq('id', meetingId)
        .select()
        .single();

      if (error) throw error;

      const parsedMeeting = {
        ...updatedMeeting,
        attendees: typeof updatedMeeting.attendees === 'string' 
          ? JSON.parse(updatedMeeting.attendees) 
          : updatedMeeting.attendees || []
      };

      setData(prev => ({
        ...prev,
        weeklyAttendance: prev.weeklyAttendance.map(m => 
          m.id === meetingId ? parsedMeeting : m
        )
      }));

      return parsedMeeting;
    } catch (error) {
      console.error('Error deleting meeting attendance:', error);
      throw error;
    }
  };

  // Get attendance statistics for all members
  const getAttendanceStats = () => {
    const completedMeetings = (data.weeklyAttendance || []).filter(m => !m.isActive);
    if (completedMeetings.length === 0) return [];

    return (data.members || []).map(member => {
      const attendedMeetings = completedMeetings.filter(meeting => 
        (Array.isArray(meeting.attendees) ? meeting.attendees : [])
          .some(a => a.memberId === member.id)
      );
      
      const attendanceCount = attendedMeetings.length;
      const attendanceRate = completedMeetings.length > 0 
        ? Math.round((attendanceCount / completedMeetings.length) * 100) 
        : 0;

      const onTimeMeetings = attendedMeetings.filter(meeting => {
        const attendance = meeting.attendees.find(a => a.memberId === member.id);
        return attendance && attendance.minutesLate === 0;
      });

      const punctualityRate = attendanceCount > 0 
        ? Math.round((onTimeMeetings.length / attendanceCount) * 100) 
        : 0;

      const totalMinutesLate = attendedMeetings.reduce((total, meeting) => {
        const attendance = meeting.attendees.find(a => a.memberId === member.id);
        return total + (attendance ? attendance.minutesLate || 0 : 0);
      }, 0);

      const averageMinutesLate = attendanceCount > 0 
        ? Math.round(totalMinutesLate / attendanceCount) 
        : 0;

      return {
        ...member,
        attendanceCount,
        attendanceRate,
        punctualityRate,
        totalMinutesLate,
        averageMinutesLate
      };
    });
  };

  // Context value
  const value = {
    ...data,
    getActiveMembers,
    getMembersNeedingOutreach,
    getTodaysMeeting,
    isTuesday,
    isMeetingTime,
    isTodayMeetingCancelled,
    cancelTodaysMeeting,
    uncancelTodaysMeeting,
    checkInMember,
    endMeeting,
    addMember,
    updateMember,
    deleteMember,
    addHistoricalMeeting,
    updateHistoricalMeeting,
    deleteHistoricalMeeting,
    updateMeetingAttendance,
    deleteMeetingAttendance,
    getAttendanceStats,
    startNewQuarter,
    loadAllData
  };

  return (
    <AttendanceContext.Provider value={value}>
      {children}
    </AttendanceContext.Provider>
  );
};

// Custom hook to use the context
export const useAttendance = () => {
  const context = useContext(AttendanceContext);
  if (context === undefined) {
    throw new Error('useAttendance must be used within an AttendanceProvider');
  }
  return context;
};